import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Stack,
  AppBar,
  Toolbar,
  Divider,
  useTheme,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link as MuiLink,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useNavigate } from 'react-router-dom';
import {
  Login as LoginIcon,
  PersonAdd,
  Pets,
  LocalHospital,
  CalendarMonth,
  CheckCircle,
  Security,
  Notifications,
  Description,
  Chat,
  Emergency,
  VerifiedUser,
  Speed,
  MedicalServices,
  ExpandMore as ExpandMoreIcon,
  Phone,
  Email,
  Facebook,
  Instagram,
  Twitter,
  Schedule,
  HealthAndSafety,
  Article,
} from '@mui/icons-material';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    { icon: <CalendarMonth sx={{ fontSize: 40 }} />, title: 'Programari Online 24/7', desc: 'Programeaza vizite oricand, de oriunde, fara sa suni' },
    { icon: <Article sx={{ fontSize: 40 }} />, title: 'Istoric Medical Complet', desc: 'Tot istoricul medical al animalului tau intr-un singur loc' },
    { icon: <Notifications sx={{ fontSize: 40 }} />, title: 'Notificari Automate', desc: 'Reminder-e pentru vaccinuri, tratamente si programari' },
    { icon: <Description sx={{ fontSize: 40 }} />, title: 'Documente Digitale', desc: 'Facturi, retete si certificate accesibile oricand' },
    { icon: <Chat sx={{ fontSize: 40 }} />, title: 'Mesaje catre Medic', desc: 'Comunicare directa cu medicul veterinar' },
    { icon: <Emergency sx={{ fontSize: 40 }} />, title: 'Info Urgente', desc: 'Date medicale esentiale pentru situatii de urgenta' },
  ];

  const howItWorksSteps = [
    { label: 'Creezi cont gratuit', desc: 'Inregistrare rapida in 2 minute' },
    { label: 'Adaugi animalul tau', desc: 'Completezi datele de baza ale animalului' },
    { label: 'Alegi clinica si medicul', desc: 'Din reteaua noastra de 85+ clinici' },
    { label: 'Programezi vizita', desc: 'Selectezi data si ora potrivita' },
    { label: 'Primesti confirmarea', desc: 'Notificari si reminder-e automate' },
  ];

  const forWho = [
    {
      icon: <Pets sx={{ fontSize: 50, color: 'primary.main' }} />,
      title: 'Pentru Stapani',
      desc: 'Gestionezi sanatatea animalului tau, programari simple, acces la istoric medical complet',
      action: 'Inregistreaza-te gratuit',
    },
    {
      icon: <LocalHospital sx={{ fontSize: 50, color: 'primary.main' }} />,
      title: 'Pentru Clinici',
      desc: 'Automatizare programari, gestionare dosare, comunicare eficienta cu clientii',
      action: 'Devino partener',
    },
    {
      icon: <MedicalServices sx={{ fontSize: 50, color: 'primary.main' }} />,
      title: 'Pentru Veterinari',
      desc: 'Acces rapid la fise medicale, istoric complet, instrumente profesionale',
      action: 'Afla mai mult',
    },
  ];

  const trustBadges = [
    { icon: <Security />, text: 'Date criptate si securizate' },
    { icon: <VerifiedUser />, text: 'Conform GDPR' },
    { icon: <CheckCircle />, text: 'Clinici verificate si autorizate' },
    { icon: <HealthAndSafety />, text: 'Suport in limba romana' },
  ];

  const faqs = [
    {
      q: 'Cat costa utilizarea platformei?',
      a: 'Pentru stapanii de animale, platforma este complet gratuita! Poti crea cont, adauga animalele tale si programa vizite fara niciun cost.',
    },
    {
      q: 'Cum adaug clinica mea preferata daca nu este in lista?',
      a: 'Poti sugera clinica ta preferata prin formularul de contact sau poti contacta direct clinica sa se inscrie in platforma. Extindem constant reteaua de parteneri.',
    },
    {
      q: 'Ce se intampla daca schimb clinica?',
      a: 'Istoricul medical al animalului tau ramane salvat in contul tau. Poti accesa toate informatiile indiferent de clinica aleasa.',
    },
    {
      q: 'Pot vedea istoricul de la alte clinici?',
      a: 'Da! Toate vizitele si tratamentele efectuate prin platforma sunt centralizate in dosarul animalului tau, indiferent de clinica.',
    },
    {
      q: 'Este sigur sa tin datele medicale ale animalului online?',
      a: 'Absolut! Folosim criptare de nivel bancar, respectam standardele GDPR si facem backup-uri regulate. Datele tale sunt mai in siguranta decat pe hartie.',
    },
    {
      q: 'Cum primesc notificari pentru vaccinuri?',
      a: 'Odata ce medicul introduce vaccinurile in fisa medicala, sistemul calculeaza automat urmatoarea doza si iti trimite reminder-e prin email si notificari in aplicatie.',
    },
  ];

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* HEADER FIX */}
      <AppBar
        position="fixed"
        elevation={scrolled ? 2 : 0}
        sx={{
          bgcolor: scrolled ? 'background.paper' : 'transparent',
          backdropFilter: scrolled ? 'blur(10px)' : 'none',
          borderBottom: scrolled ? `1px solid ${theme.palette.divider}` : 'none',
          transition: 'all 0.3s',
        }}
      >
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, cursor: 'pointer' }} onClick={() => navigate('/')}>
            <Box
              component="img"
              src="/assets/img/Logo.png"
              alt="Healthy Paws"
              sx={{ height: 40, mr: 2 }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: scrolled ? 'text.primary' : 'white',
                textShadow: scrolled ? 'none' : '1px 1px 2px rgba(0,0,0,0.3)',
              }}
            >
              HEALTHY PAWS
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<PersonAdd />}
              onClick={() => navigate('/register')}
              sx={{
                borderColor: scrolled ? 'primary.main' : 'white',
                color: scrolled ? 'primary.main' : 'white',
                '&:hover': {
                  borderColor: scrolled ? 'primary.dark' : 'white',
                  bgcolor: scrolled ? alpha(theme.palette.primary.main, 0.1) : alpha('#fff', 0.1),
                },
              }}
            >
              Inregistrare
            </Button>
            <Button
              variant="contained"
              startIcon={<LoginIcon />}
              onClick={() => navigate('/login')}
              sx={{
                bgcolor: scrolled ? 'primary.main' : 'white',
                color: scrolled ? 'white' : 'primary.main',
                '&:hover': {
                  bgcolor: scrolled ? 'primary.dark' : alpha('#fff', 0.9),
                },
              }}
            >
              Conectare
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* HERO SECTION */}
      <Box
        sx={{
          position: 'relative',
          pt: { xs: 15, md: 20 },
          pb: { xs: 10, md: 15 },
          backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.85)} 0%, ${alpha(theme.palette.secondary.main, 0.85)} 100%), url(/assets/img/hero-bg.jpg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'white',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                }}
              >
                Sanatatea Animalului Tau, Simplificata
              </Typography>
              <Typography variant="h5" sx={{ mb: 1, opacity: 0.95, textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
                Connecting Pets and People
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem', opacity: 0.9, maxWidth: 600 }}>
                Platforma completa pentru gestionarea sanatatii animalului tau: programari online, istoric medical digital, notificari automate si comunicare directa cu medicul veterinar.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PersonAdd />}
                  onClick={() => navigate('/register')}
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': { bgcolor: alpha('#fff', 0.9) },
                  }}
                >
                  Incepe Gratuit
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: alpha('#fff', 0.1),
                    },
                  }}
                >
                  Vezi Cum Functioneaza
                </Button>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Paper
                elevation={10}
                sx={{
                  p: 3,
                  bgcolor: alpha('#fff', 0.95),
                  borderRadius: 3,
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CalendarMonth sx={{ color: 'primary.main', fontSize: 32 }} />
                    <Typography fontWeight={600} color="text.primary">Programari instant</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Article sx={{ color: 'primary.main', fontSize: 32 }} />
                    <Typography fontWeight={600} color="text.primary">Dosar medical digital</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Notifications sx={{ color: 'primary.main', fontSize: 32 }} />
                    <Typography fontWeight={600} color="text.primary">Reminder-e automate</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Emergency sx={{ color: 'primary.main', fontSize: 32 }} />
                    <Typography fontWeight={600} color="text.primary">Suport pentru urgente</Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* TRUST BADGES */}
      <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), py: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={3} justifyContent="center">
            {trustBadges.map((badge, idx) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={idx}>
                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center">
                  <Box sx={{ color: 'primary.main' }}>{badge.icon}</Box>
                  <Typography variant="body2" fontWeight={600}>{badge.text}</Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CUM FUNCTIONEAZA */}
      <Container maxWidth="md" sx={{ py: 10 }} id="how-it-works">
        <Typography variant="h3" align="center" sx={{ mb: 2, fontWeight: 700 }}>
          Cum Functioneaza?
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 6, maxWidth: 600, mx: 'auto' }}>
          Doar 5 pasi simpli te despart de managementul complet al sanatatii animalului tau
        </Typography>

        <Stepper orientation="vertical">
          {howItWorksSteps.map((step, index) => (
            <Step key={index} active completed>
              <StepLabel
                StepIconComponent={() => (
                  <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40, fontWeight: 700 }}>
                    {index + 1}
                  </Avatar>
                )}
              >
                <Typography variant="h6" fontWeight={600}>{step.label}</Typography>
              </StepLabel>
              <StepContent>
                <Typography color="text.secondary" sx={{ mb: 2 }}>{step.desc}</Typography>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Container>

      {/* PENTRU CINE ESTE */}
      <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), py: 10 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" sx={{ mb: 2, fontWeight: 700 }}>
            Pentru Cine Este?
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 6 }}>
            HealthyPaws se adreseaza tuturor celor implicati in ingrijirea animalelor
          </Typography>

          <Grid container spacing={4}>
            {forWho.map((item, idx) => (
              <Grid size={{ xs: 12, md: 4 }} key={idx}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    border: `2px solid ${theme.palette.divider}`,
                    borderRadius: 3,
                    textAlign: 'center',
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      transform: 'translateY(-8px)',
                      boxShadow: theme.shadows[8],
                    },
                  }}
                >
                  <Box sx={{ mb: 2 }}>{item.icon}</Box>
                  <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>{item.title}</Typography>
                  <Typography color="text.secondary" sx={{ mb: 3 }}>{item.desc}</Typography>
                  <Button variant="outlined" fullWidth>{item.action}</Button>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CE POTI FACE */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography variant="h3" align="center" sx={{ mb: 2, fontWeight: 700 }}>
          Ce Poti Face cu HealthyPaws?
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 6 }}>
          Toate instrumentele de care ai nevoie pentru ingrijirea completa a animalului tau
        </Typography>

        <Grid container spacing={3}>
          {features.map((feature, idx) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={idx}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  transition: 'all 0.3s',
                  '&:hover': {
                    boxShadow: theme.shadows[4],
                    borderColor: 'primary.main',
                  },
                }}
              >
                <Box sx={{ color: 'primary.main', mb: 2 }}>{feature.icon}</Box>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>{feature.title}</Typography>
                <Typography variant="body2" color="text.secondary">{feature.desc}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* STATISTICI */}
      <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {[
              { value: '85+', label: 'Clinici Partenere', sublabel: 'In toata tara' },
              { value: '1000+', label: 'Carnete de Sanatate', sublabel: 'Digitale active' },
              { value: '24/7', label: 'Disponibilitate', sublabel: 'Program oricand' },
              { value: '1K+', label: 'Clienti Multumiti', sublabel: 'Si tot creste' },
            ].map((stat, idx) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={idx}>
                <Paper elevation={0} sx={{ p: 3, textAlign: 'center', bgcolor: 'background.paper' }}>
                  <Typography variant="h3" fontWeight={800} color="primary.main" sx={{ mb: 1 }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>{stat.label}</Typography>
                  <Typography variant="body2" color="text.secondary">{stat.sublabel}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ */}
      <Container maxWidth="md" sx={{ py: 10 }}>
        <Typography variant="h3" align="center" sx={{ mb: 2, fontWeight: 700 }}>
          Intrebari Frecvente
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 6 }}>
          Raspunsuri la cele mai comune intrebari
        </Typography>

        {faqs.map((faq, idx) => (
          <Accordion key={idx} sx={{ mb: 2, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>{faq.q}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="text.secondary">{faq.a}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Container>

      {/* CTA FINAL */}
      <Box
        sx={{
          py: 10,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: 'white',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" align="center" sx={{ mb: 2, fontWeight: 700 }}>
            Gata sa Incepi?
          </Typography>
          <Typography variant="h6" align="center" sx={{ mb: 4, opacity: 0.95 }}>
            Inregistreaza-te acum si ofera-i animalului tau cele mai bune servicii medicale veterinare!
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              startIcon={<PersonAdd />}
              onClick={() => navigate('/register')}
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                '&:hover': { bgcolor: alpha('#fff', 0.9) },
              }}
            >
              Inregistreaza-te Gratuit
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<LoginIcon />}
              onClick={() => navigate('/login')}
              sx={{
                borderColor: 'white',
                color: 'white',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: alpha('#fff', 0.1),
                },
              }}
            >
              Conecteaza-te
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* FOOTER */}
      <Box component="footer" sx={{ bgcolor: 'background.paper', borderTop: `1px solid ${theme.palette.divider}`, py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Logo & Descriere */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box component="img" src="/assets/img/Logo.png" alt="Healthy Paws" sx={{ height: 40, mr: 2 }} />
                <Typography variant="h6" fontWeight={700}>HEALTHY PAWS</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Platforma dedicata ingrijirii si sanatatii animalelor de companie.
                Conectam stapanii cu veterinarii de incredere.
              </Typography>
              <Stack direction="row" spacing={1}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, cursor: 'pointer' }}>
                  <Facebook sx={{ fontSize: 18 }} />
                </Avatar>
                <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, cursor: 'pointer' }}>
                  <Instagram sx={{ fontSize: 18 }} />
                </Avatar>
                <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, cursor: 'pointer' }}>
                  <Twitter sx={{ fontSize: 18 }} />
                </Avatar>
              </Stack>
            </Grid>

            {/* Link-uri Rapide */}
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Link-uri Utile</Typography>
              <Stack spacing={1}>
                <MuiLink href="#" color="text.secondary" underline="hover">Despre Noi</MuiLink>
                <MuiLink href="#" color="text.secondary" underline="hover">Servicii</MuiLink>
                <MuiLink href="#" color="text.secondary" underline="hover">Clinici Partenere</MuiLink>
                <MuiLink href="#" color="text.secondary" underline="hover">Blog</MuiLink>
              </Stack>
            </Grid>

            {/* Suport */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Suport</Typography>
              <Stack spacing={1}>
                <MuiLink href="#" color="text.secondary" underline="hover">Ajutor</MuiLink>
                <MuiLink href="#" color="text.secondary" underline="hover">Termeni si Conditii</MuiLink>
                <MuiLink href="#" color="text.secondary" underline="hover">Politica Confidentialitate</MuiLink>
                <MuiLink href="#" color="text.secondary" underline="hover">GDPR</MuiLink>
              </Stack>
            </Grid>

            {/* Contact */}
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Contact</Typography>
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Email sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">contact@healthypaws.ro</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Phone sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">+40 XXX XXX XXX</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Schedule sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">Suport: L-V, 9:00-18:00</Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Typography variant="body2" align="center" color="text.secondary">
            © {new Date().getFullYear()} Healthy Paws. Toate drepturile rezervate.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
